import React, { useState } from "react";
import {
  Row,
  Col,
  Container,
  Card,
  Button,
  Form,
  Alert,
  Carousel,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  FaFacebookSquare,
  FaChartLine,
  FaHeadset,
  FaShoppingCart,
  FaArrowRight,
} from "react-icons/fa";

const LandingPage = () => {
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertVariant, setAlertVariant] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [registrationForm, setRegistrationForm] = useState({
    pageName: "",
    pageAccountName: "",
    facebookUrl: "",
    lineUrl: "",
    contactName: "",
    contactPhone: "",
    pageCategory: "",
    package: "",
    remark: "",
  });
  const [validated, setValidated] = useState(false);

  const handleRegistrationChange = (e) => {
    const { name, value } = e.target;
    setRegistrationForm({
      ...registrationForm,
      [name]: value,
    });
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors({
        ...fieldErrors,
        [name]: undefined,
      });
    }
  };

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^0[0-9]{9}$/;
    return phoneRegex.test(phone.replace(/[-\s]/g, ""));
  };

  const validateFacebookUrl = (url) => {
    if (!url) return true; // Optional field
    const facebookRegex = /^https:\/\/(www\.)?(facebook\.com|fb\.com)\/.+/;
    return facebookRegex.test(url);
  };

  const validateLineUrl = (url) => {
    if (!url) return true; // Optional field
    const lineRegex = /^https:\/\/(line\.me\/ti\/p\/|lin\.ee\/).+/;
    return lineRegex.test(url);
  };

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    // Basic HTML5 validation
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    // Custom validations
    const errors = {};

    // Validate phone number
    if (!validatePhoneNumber(registrationForm.contactPhone)) {
      errors.contactPhone =
        "เบอร์โทรศัพท์ต้องเป็นเลข 10 หลัก เริ่มด้วย 0 (เช่น 0812345678)";
    }

    // Validate Facebook URL
    if (!validateFacebookUrl(registrationForm.facebookUrl)) {
      errors.facebookUrl =
        "URL Facebook ไม่ถูกต้อง (ต้องเป็น https://facebook.com/... หรือ https://fb.com/...)";
    }

    // Validate LINE URL
    if (!validateLineUrl(registrationForm.lineUrl)) {
      errors.lineUrl =
        "URL LINE ไม่ถูกต้อง (ต้องเป็น https://line.me/... หรือ https://lin.ee/...)";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setIsSubmitting(true);
    setFieldErrors({});

    try {
      // Prepare data for API
      const apiData = {
        pageName: registrationForm.pageName.trim(),
        pageAccountName: registrationForm.pageAccountName.trim(),
        contactName: registrationForm.contactName.trim(),
        contactPhone: registrationForm.contactPhone.replace(/[-\s]/g, ""),
        pageCategory: registrationForm.pageCategory,
        package: registrationForm.package,
        facebookUrl: registrationForm.facebookUrl.trim() || undefined,
        lineUrl: registrationForm.lineUrl.trim() || undefined,
        remark: registrationForm.remark.trim() || undefined,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/public/page-registration`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(apiData),
        },
      );

      const data = await response.json();

      if (response.ok) {
        // Success
        setAlertVariant("success");
        setAlertMessage(
          data.message ||
            "ส่งคำขอลงทะเบียนเรียบร้อยแล้ว ทีมงานจะติดต่อกลับภายใน 24 ชั่วโมง",
        );
        setShowAlert(true);

        // Reset form
        setRegistrationForm({
          pageName: "",
          pageAccountName: "",
          facebookUrl: "",
          lineUrl: "",
          contactName: "",
          contactPhone: "",
          pageCategory: "",
          package: "",
          remark: "",
        });

        // Clear field errors
        setFieldErrors({});

        // Reset validation state
        setValidated(false);

        setTimeout(() => {
          setShowAlert(false);
        }, 5000);
      } else {
        // Handle errors
        switch (response.status) {
          case 400:
            // Validation errors
            if (data.errors && Array.isArray(data.errors)) {
              const fieldErrors = {};
              data.errors.forEach((error) => {
                fieldErrors[error.field] = error.message;
              });
              setFieldErrors(fieldErrors);
            }
            setAlertVariant("danger");
            setAlertMessage(data.message || "ข้อมูลไม่ถูกต้อง");
            break;

          case 409:
            // Duplicate registration
            setAlertVariant("warning");
            setAlertMessage(
              data.message ||
                "คำขอของคุณอยู่ระหว่างรอดำเนินการ กรุณารอการติดต่อจากทีมงาน",
            );
            break;

          case 429:
            // Rate limit exceeded
            const minutes = Math.ceil((data.retryAfter || 3600) / 60);
            setAlertVariant("warning");
            setAlertMessage(
              `${data.message || "คุณส่งคำขอมากเกินไป"} (รออีก ${minutes} นาที)`,
            );
            break;

          case 500:
            // Server error
            setAlertVariant("danger");
            setAlertMessage(
              data.message ||
                "เกิดข้อผิดพลาดในระบบ กรุณาลองใหม่อีกครั้งภายหลัง",
            );
            break;

          default:
            setAlertVariant("danger");
            setAlertMessage(data.message || "เกิดข้อผิดพลาดที่ไม่คาดคิด");
        }
        setShowAlert(true);
      }
    } catch (error) {
      // Network error
      setAlertVariant("danger");
      setAlertMessage(
        "ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต",
      );
      setShowAlert(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="landing-page">
        {/* Hero Section */}
        <section
          className="hero-section"
          style={{ backgroundColor: "#f77c1c", padding: "80px 0" }}
        >
          <Container>
            <Row className="align-items-center">
              <Col lg="6" className="text-white">
                <h1 className="display-4 fw-bold mb-4">
                  Boost Ads Service for Online Stores
                </h1>
                <p className="lead mb-4">
                  Increase sales, reach new customers with Facebook Boost Ads
                  service from our professional team
                </p>
                <div className="d-flex gap-3">
                  <Link to="/portal/summary">
                    <Button variant="light" size="lg">
                      Login
                    </Button>
                  </Link>
                  <Button variant="outline-light" size="lg" href="#register">
                    Register
                  </Button>
                </div>
              </Col>
              <Col lg="6" className="mt-5 mt-lg-0 text-center">
                <img
                  src="/assets/images/landing/hero-image.png"
                  alt="Facebook Ads Boost"
                  className="img-fluid rounded shadow"
                  style={{ maxHeight: "400px" }}
                />
              </Col>
            </Row>
          </Container>
        </section>

        {/* Features Section */}
        <section id="features" className="features-section py-5">
          <Container>
            <Row className="text-center mb-5">
              <Col>
                <h2 className="fw-bold">Our Services</h2>
                <p className="lead">
                  Complete solutions for Facebook advertising
                </p>
              </Col>
            </Row>

            <Row>
              <Col md="4" className="mb-4">
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Body className="text-center p-4">
                    <div className="feature-icon mb-3">
                      <FaFacebookSquare size={50} className="text-primary" />
                    </div>
                    <h4>Boost Live</h4>
                    <p>
                      Live stream boosting service to increase viewers and sales
                      opportunities
                    </p>
                  </Card.Body>
                </Card>
              </Col>

              <Col md="4" className="mb-4">
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Body className="text-center p-4">
                    <div className="feature-icon mb-3">
                      <FaChartLine size={50} className="text-primary" />
                    </div>
                    <h4>Boost Post</h4>
                    <p>
                      Post boosting service to increase visibility and
                      engagement
                    </p>
                  </Card.Body>
                </Card>
              </Col>

              <Col md="4" className="mb-4">
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Body className="text-center p-4">
                    <div className="feature-icon mb-3">
                      <FaHeadset size={50} className="text-primary" />
                    </div>
                    <h4>Consult</h4>
                    <p>Facebook advertising consultation service by experts</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Packages Section */}
        <section
          id="packages"
          className="packages-section py-5"
          style={{ backgroundColor: "#f8f9fa" }}
        >
          <Container>
            <Row className="text-center mb-5">
              <Col>
                <h2 className="fw-bold">Popular Packages</h2>
                <p className="lead">
                  Choose the package that suits your business
                </p>
              </Col>
            </Row>

            <Row>
              <Col lg="3" md="6" className="mb-4">
                <Card className="h-100 border-0 shadow-sm">
                  <div
                    className="text-center p-3"
                    style={{ backgroundColor: "#f77c1c", color: "white" }}
                  >
                    <h4>Complete Ads Package</h4>
                  </div>
                  <Card.Body className="text-center">
                    <h2 className="mb-4">20,000 THB</h2>
                    <ul className="list-unstyled">
                      <li className="mb-2">✓ 10 Boost Live sessions</li>
                      <li className="mb-2">✓ 15 Boost Post sessions</li>
                      <li className="mb-2">✓ 2 Consultation sessions</li>
                      <li className="mb-2">✓ Valid for 3 months</li>
                    </ul>
                    <Button variant="primary" className="mt-3 w-100">
                      <FaShoppingCart className="me-2" /> Purchase
                    </Button>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg="3" md="6" className="mb-4">
                <Card className="h-100 border-0 shadow-sm">
                  <div
                    className="text-center p-3"
                    style={{ backgroundColor: "#f77c1c", color: "white" }}
                  >
                    <h4>Live Stream Package</h4>
                  </div>
                  <Card.Body className="text-center">
                    <h2 className="mb-4">15,900 THB</h2>
                    <ul className="list-unstyled">
                      <li className="mb-2">✓ 10 Boost Live sessions</li>
                      <li className="mb-2">✓ 1 Consultation session</li>
                      <li className="mb-2">✓ Valid for 3 months</li>
                      <li className="mb-2">✓ Managed by experts</li>
                    </ul>
                    <Button variant="primary" className="mt-3 w-100">
                      <FaShoppingCart className="me-2" /> Purchase
                    </Button>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg="3" md="6" className="mb-4">
                <Card className="h-100 border-0 shadow-sm">
                  <div
                    className="text-center p-3"
                    style={{ backgroundColor: "#f77c1c", color: "white" }}
                  >
                    <h4>Post Package</h4>
                  </div>
                  <Card.Body className="text-center">
                    <h2 className="mb-4">12,900 THB</h2>
                    <ul className="list-unstyled">
                      <li className="mb-2">✓ 15 Boost Post sessions</li>
                      <li className="mb-2">✓ 1 Consultation session</li>
                      <li className="mb-2">✓ Valid for 3 months</li>
                      <li className="mb-2">✓ Managed by experts</li>
                    </ul>
                    <Button variant="primary" className="mt-3 w-100">
                      <FaShoppingCart className="me-2" /> Purchase
                    </Button>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg="3" md="6" className="mb-4">
                <Card className="h-100 border-0 shadow-sm">
                  <div
                    className="text-center p-3"
                    style={{ backgroundColor: "#f77c1c", color: "white" }}
                  >
                    <h4>Image+Caption Package</h4>
                  </div>
                  <Card.Body className="text-center">
                    <h2 className="mb-4">5,500 THB</h2>
                    <ul className="list-unstyled">
                      <li className="mb-2">✓ 10 Image designs</li>
                      <li className="mb-2">✓ 10 Caption writings</li>
                      <li className="mb-2">✓ Valid for 2 months</li>
                      <li className="mb-2">
                        ✓ Managed by professional designers
                      </li>
                    </ul>
                    <Button variant="primary" className="mt-3 w-100">
                      <FaShoppingCart className="me-2" /> Purchase
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Testimonials Section */}
        <section className="testimonials-section py-5">
          <Container>
            <Row className="text-center mb-5">
              <Col>
                <h2 className="fw-bold">Our Clients</h2>
                <p className="lead">Testimonials from actual users</p>
              </Col>
            </Row>

            <Row>
              <Col md="12">
                <Carousel indicators={false}>
                  <Carousel.Item>
                    <Row className="justify-content-center">
                      <Col md="8">
                        <Card className="border-0 shadow-sm">
                          <Card.Body className="text-center p-5">
                            <div className="mb-4">
                              <img
                                src="/assets/images/landing/testimonial-1.jpg"
                                alt="Testimonial"
                                className="rounded-circle"
                                width="80"
                                height="80"
                              />
                            </div>
                            <p className="lead mb-4">
                              "Excellent service. The team pays attention to
                              every detail. Sales increased significantly after
                              using the Boost Live service."
                            </p>
                            <h5>Napha</h5>
                            <p className="text-muted">
                              Owner of Beauty Shop Online
                            </p>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </Carousel.Item>

                  <Carousel.Item>
                    <Row className="justify-content-center">
                      <Col md="8">
                        <Card className="border-0 shadow-sm">
                          <Card.Body className="text-center p-5">
                            <div className="mb-4">
                              <img
                                src="/assets/images/landing/testimonial-2.jpg"
                                alt="Testimonial"
                                className="rounded-circle"
                                width="80"
                                height="80"
                              />
                            </div>
                            <p className="lead mb-4">
                              "Great value. Invested just over ten thousand
                              baht, but sales increased by hundreds of
                              thousands. Thanks to the team for taking such good
                              care."
                            </p>
                            <h5>Somchai</h5>
                            <p className="text-muted">
                              Owner of Fashion Style brand
                            </p>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </Carousel.Item>

                  <Carousel.Item>
                    <Row className="justify-content-center">
                      <Col md="8">
                        <Card className="border-0 shadow-sm">
                          <Card.Body className="text-center p-5">
                            <div className="mb-4">
                              <img
                                src="/assets/images/landing/testimonial-3.jpg"
                                alt="Testimonial"
                                className="rounded-circle"
                                width="80"
                                height="80"
                              />
                            </div>
                            <p className="lead mb-4">
                              "Very impressed. The team is friendly and provides
                              lots of good advice, helping me understand
                              advertising better."
                            </p>
                            <h5>Wipha</h5>
                            <p className="text-muted">
                              Owner of Healthy Food shop
                            </p>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </Carousel.Item>
                </Carousel>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Registration Section */}
        <section
          id="register"
          className="registration-section py-5"
          style={{ backgroundColor: "#f8f9fa" }}
        >
          <Container>
            <Row className="justify-content-center">
              <Col lg="8">
                <Card className="border-0 shadow">
                  <Card.Body className="p-5">
                    <h2 className="text-center mb-4">Register for Service</h2>

                    {showAlert && (
                      <Alert
                        variant={alertVariant}
                        onClose={() => setShowAlert(false)}
                        dismissible
                      >
                        {alertMessage}
                      </Alert>
                    )}

                    <Form
                      noValidate
                      validated={validated}
                      onSubmit={handleRegistrationSubmit}
                    >
                      <Row>
                        <Col md="6">
                          <Form.Group className="mb-3" controlId="formPageName">
                            <Form.Label>Facebook Page Name</Form.Label>
                            <Form.Control
                              type="text"
                              name="pageName"
                              value={registrationForm.pageName}
                              onChange={handleRegistrationChange}
                              placeholder="Enter page name"
                              required
                            />
                            <Form.Control.Feedback type="invalid">
                              Please enter your page name
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>

                        <Col md="6">
                          <Form.Group
                            className="mb-3"
                            controlId="formPageAccount"
                          >
                            <Form.Label>Facebook Account Name</Form.Label>
                            <Form.Control
                              type="text"
                              name="pageAccountName"
                              value={registrationForm.pageAccountName}
                              onChange={handleRegistrationChange}
                              placeholder="Enter Facebook account name"
                              required
                              isInvalid={!!fieldErrors.pageAccountName}
                            />
                            <Form.Control.Feedback type="invalid">
                              Please enter your Facebook account name
                            </Form.Control.Feedback>
                            {fieldErrors.pageAccountName && (
                              <Form.Text className="text-danger">
                                {fieldErrors.pageAccountName}
                              </Form.Text>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group className="mb-3" controlId="formPageUrl">
                        <Form.Label>Facebook Page URL</Form.Label>
                        <Form.Control
                          type="url"
                          name="facebookUrl"
                          value={registrationForm.facebookUrl}
                          onChange={handleRegistrationChange}
                          placeholder="Enter your Facebook page URL"
                          isInvalid={!!fieldErrors.facebookUrl}
                        />
                        <Form.Control.Feedback type="invalid">
                          Please enter a valid Facebook page URL
                        </Form.Control.Feedback>
                        {fieldErrors.facebookUrl && (
                          <Form.Text className="text-danger">
                            {fieldErrors.facebookUrl}
                          </Form.Text>
                        )}
                      </Form.Group>

                      <Form.Group className="mb-3" controlId="formLineUrl">
                        <Form.Label>LINE URL (if any)</Form.Label>
                        <Form.Control
                          type="url"
                          name="lineUrl"
                          value={registrationForm.lineUrl}
                          onChange={handleRegistrationChange}
                          placeholder="Enter your LINE URL"
                          isInvalid={!!fieldErrors.lineUrl}
                        />
                        {fieldErrors.lineUrl && (
                          <Form.Text className="text-danger">
                            {fieldErrors.lineUrl}
                          </Form.Text>
                        )}
                      </Form.Group>

                      <Row>
                        <Col md="6">
                          <Form.Group
                            className="mb-3"
                            controlId="formCustomerName"
                          >
                            <Form.Label>Contact Name</Form.Label>
                            <Form.Control
                              type="text"
                              name="contactName"
                              value={registrationForm.contactName}
                              onChange={handleRegistrationChange}
                              placeholder="Enter contact name"
                              required
                              isInvalid={!!fieldErrors.contactName}
                            />
                            <Form.Control.Feedback type="invalid">
                              Please enter contact name
                            </Form.Control.Feedback>
                            {fieldErrors.contactName && (
                              <Form.Text className="text-danger">
                                {fieldErrors.contactName}
                              </Form.Text>
                            )}
                          </Form.Group>
                        </Col>

                        <Col md="6">
                          <Form.Group
                            className="mb-3"
                            controlId="formTelephone"
                          >
                            <Form.Label>Phone Number</Form.Label>
                            <Form.Control
                              type="tel"
                              name="contactPhone"
                              value={registrationForm.contactPhone}
                              onChange={handleRegistrationChange}
                              placeholder="0812345678"
                              required
                              isInvalid={!!fieldErrors.contactPhone}
                            />
                            <Form.Control.Feedback type="invalid">
                              Please enter your phone number
                            </Form.Control.Feedback>
                            {fieldErrors.contactPhone && (
                              <Form.Text className="text-danger">
                                {fieldErrors.contactPhone}
                              </Form.Text>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md="6">
                          <Form.Group className="mb-3" controlId="formCategory">
                            <Form.Label>Page Category</Form.Label>
                            <Form.Select
                              name="pageCategory"
                              value={registrationForm.pageCategory}
                              onChange={handleRegistrationChange}
                              required
                              isInvalid={!!fieldErrors.pageCategory}
                            >
                              <option value="">Select category</option>
                              <option value="E-commerce">E-commerce</option>
                              <option value="Local Business">
                                Local Business
                              </option>
                              <option value="Media">Media</option>
                              <option value="Entertainment">
                                Entertainment
                              </option>
                              <option value="Other">Other</option>
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">
                              Please select a page category
                            </Form.Control.Feedback>
                            {fieldErrors.pageCategory && (
                              <Form.Text className="text-danger">
                                {fieldErrors.pageCategory}
                              </Form.Text>
                            )}
                          </Form.Group>
                        </Col>

                        <Col md="6">
                          <Form.Group
                            className="mb-3"
                            controlId="formInterestedPackage"
                          >
                            <Form.Label>Package of Interest</Form.Label>
                            <Form.Select
                              name="package"
                              value={registrationForm.package}
                              onChange={handleRegistrationChange}
                              required
                              isInvalid={!!fieldErrors.package}
                            >
                              <option value="">Select package</option>
                              <option value="Complete Ads Package">
                                Complete Ads Package
                              </option>
                              <option value="Live Stream Package">
                                Live Stream Package
                              </option>
                              <option value="Post Package">Post Package</option>
                              <option value="Image+Caption Package">
                                Image+Caption Package
                              </option>
                              <option value="Not sure yet">Not sure yet</option>
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">
                              Please select a package of interest
                            </Form.Control.Feedback>
                            {fieldErrors.package && (
                              <Form.Text className="text-danger">
                                {fieldErrors.package}
                              </Form.Text>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md="12">
                          <Form.Group className="mb-3" controlId="formRemark">
                            <Form.Label>
                              Additional Remarks (Optional)
                            </Form.Label>
                            <Form.Control
                              as="textarea"
                              name="remark"
                              value={registrationForm.remark}
                              onChange={handleRegistrationChange}
                              placeholder="Any additional notes or requirements..."
                              rows={3}
                              maxLength={500}
                            />
                            <Form.Text className="text-muted">
                              {registrationForm.remark.length}/500 characters
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>

                      <div className="text-center mt-4">
                        <Button
                          variant="primary"
                          type="submit"
                          size="lg"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Submitting...
                            </>
                          ) : (
                            <>
                              Register <FaArrowRight className="ms-2" />
                            </>
                          )}
                        </Button>
                      </div>
                    </Form>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Footer */}
        <footer
          className="py-4"
          style={{ backgroundColor: "#333", color: "white" }}
        >
          <Container>
            <Row>
              <Col md="4" className="mb-4 mb-md-0">
                <h5>About Us</h5>
                <p>
                  Ruaysure Company Limited provides Facebook Boost Ads services
                  for online stores with a professional team
                </p>
              </Col>

              <Col md="4" className="mb-4 mb-md-0">
                <h5>Contact Us</h5>
                <p>Tel: 02-123-4567</p>
                <p>Email: contact@ruaysure.com</p>
                <p>Line: @ruaysure</p>
              </Col>

              <Col md="4">
                <h5>Quick Links</h5>
                <ul className="list-unstyled">
                  <li>
                    <a href="/" className="text-white">
                      Home
                    </a>
                  </li>
                  <li>
                    <a href="#features" className="text-white">
                      Our Services
                    </a>
                  </li>
                  <li>
                    <a href="#packages" className="text-white">
                      Packages
                    </a>
                  </li>
                  <li>
                    <a href="#register" className="text-white">
                      Register
                    </a>
                  </li>
                  <li>
                    <Link to="/dashboard" className="text-white">
                      Login
                    </Link>
                  </li>
                </ul>
              </Col>
            </Row>

            <hr className="my-4" style={{ backgroundColor: "#555" }} />

            <Row>
              <Col className="text-center">
                <p className="mb-0">
                  © 2025 Ruaysure Company Limited. All rights reserved.
                </p>
              </Col>
            </Row>
          </Container>
        </footer>
      </div>
    </>
  );
};

export default LandingPage;
